/**
 * DiagnosisSelect
 *
 * Two synced fields for entering a diagnosis:
 *  • A searchable Autocomplete for Medicare-supported ICD-10 codes.
 *  • A TextInput for the description — auto-fills when a code is chosen.
 * Historical codes outside the current ValueSet remain visible as “previously selected”.
 */

import { Autocomplete, Group, Text, TextInput, type ComboboxItem } from '@mantine/core';
import { IconClipboardList } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import {
  DIAGNOSIS_AUTOCOMPLETE_DATA,
  getDiagnosisSelectionStatus,
} from '../data/diagnosisCodes';

export interface DiagnosisSelectProps {
  code: string;
  description: string;
  onCodeChange: (code: string) => void;
  onDescriptionChange: (description: string) => void;
  /** Makes the code field required (shows asterisk, gates submit). */
  required?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  codeLabel?: string;
  descriptionLabel?: string;
  /** Optional help text under the code field. Omitted by default. */
  codeDescription?: string;
  /** Optional help text under the description field. Omitted by default. */
  descriptionNote?: string;
  disabled?: boolean;
}

const CODE_FIELD_WIDTH_PX = 220;
/** Dropdown is wider than the input so full code + description stay readable. */
const CODE_DROPDOWN_WIDTH_PX = 480;
/** Reserve space for chevron + clear so the input does not resize when a value is set. */
const CODE_AUTOCOMPLETE_RIGHT_SECTION_WIDTH = 48;
const DROPDOWN_OPTION_LIMIT = 20;

/** Pre-lowercased index so each keystroke does not re-lowercase ~800 labels. */
const DIAGNOSIS_SEARCH_INDEX: Array<ComboboxItem & { searchText: string }> =
  DIAGNOSIS_AUTOCOMPLETE_DATA.map((item) => ({
    value: item.value,
    label: item.label,
    searchText: item.label.toLowerCase(),
  }));

/**
 * Return at most `limit` options. Passing only this slice to Autocomplete avoids
 * Mantine's per-keystroke validateOptions walk over the full catalog.
 */
function filterDiagnosisOptions(search: string, limit: number): ComboboxItem[] {
  const s = search.trim().toLowerCase();
  const result: ComboboxItem[] = [];
  for (const item of DIAGNOSIS_SEARCH_INDEX) {
    if (result.length >= limit) break;
    if (!s || item.searchText.includes(s)) {
      result.push({ value: item.value, label: item.label });
    }
  }
  return result;
}

export function DiagnosisSelect({
  code,
  description,
  onCodeChange,
  onDescriptionChange,
  required,
  size,
  codeLabel = 'Diagnosis code',
  descriptionLabel = 'Description',
  codeDescription,
  descriptionNote,
  disabled,
}: DiagnosisSelectProps): JSX.Element {
  // Local input so typing stays responsive; parent form only updates on select/clear/blur.
  const [inputValue, setInputValue] = useState(code);

  useEffect(() => {
    setInputValue(code);
  }, [code]);

  const handleCodeChange = (raw: string) => {
    setInputValue(raw);
    if (!raw.trim()) {
      onCodeChange('');
    }
  };

  const handleOptionSubmit = (value: string) => {
    setInputValue(value);
    onCodeChange(value);
    // Callers auto-fill description in onCodeChange; avoid a second update that can
    // race with stale parent code state (e.g. treatment-plan questionnaire form).
  };

  const handleBlur = () => {
    if (inputValue === code) return;
    onCodeChange(inputValue);
  };

  const selectionStatus = code.trim() ? getDiagnosisSelectionStatus(code) : 'unknown';
  const autocompleteData = useMemo(() => {
    const options = filterDiagnosisOptions(inputValue, DROPDOWN_OPTION_LIMIT);
    if (selectionStatus !== 'historical' || !code.trim()) return options;

    const historicalValue = code.trim();
    if (options.some((opt) => opt.value === historicalValue)) return options;

    return [
      {
        value: historicalValue,
        label: `${historicalValue} – ${description || 'Previously selected (no longer in current Medicare list)'}`,
      },
      ...options.slice(0, DROPDOWN_OPTION_LIMIT - 1),
    ];
  }, [inputValue, selectionStatus, code, description]);

  return (
    <Group align="flex-start" gap="md" wrap="wrap">
      <Autocomplete
        label={codeLabel}
        description={codeDescription}
        placeholder="Search Medicare-supported codes…"
        value={inputValue}
        onChange={handleCodeChange}
        onOptionSubmit={handleOptionSubmit}
        onBlur={handleBlur}
        data={autocompleteData}
        clearable
        limit={DROPDOWN_OPTION_LIMIT}
        maxDropdownHeight={320}
        required={required}
        size={size}
        disabled={disabled}
        leftSection={<IconClipboardList size={14} />}
        rightSectionWidth={CODE_AUTOCOMPLETE_RIGHT_SECTION_WIDTH}
        styles={{ root: { flex: `0 0 ${CODE_FIELD_WIDTH_PX}px`, width: CODE_FIELD_WIDTH_PX } }}
        comboboxProps={{ withinPortal: true, width: CODE_DROPDOWN_WIDTH_PX }}
        error={
          selectionStatus === 'historical'
            ? 'Previously selected — no longer in the current Medicare-supported list'
            : undefined
        }
        renderOption={({ option }) => {
          const labeled = option as { value: string; label?: string };
          const fullLabel = labeled.label || labeled.value;
          return (
            <Text size="sm" style={{ whiteSpace: 'normal', lineHeight: 1.35 }} title={fullLabel}>
              {fullLabel}
            </Text>
          );
        }}
      />

      <TextInput
        label={descriptionLabel}
        description={descriptionNote}
        placeholder="Enter diagnosis name"
        value={description}
        onChange={(e) => onDescriptionChange(e.currentTarget.value)}
        size={size}
        disabled={disabled}
        styles={{ root: { flex: 1, minWidth: 220 } }}
      />
    </Group>
  );
}
